import { SVGAssets } from '@business/assets/vectors';
import socketService from '@business/services/SocketService';
import moment from 'moment-timezone';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { AIContentMarkdown } from './AIContentMarkdown';

interface LasagnaChatBotProps {
  setIsOpenChatBot: (value: boolean) => void;
  isMinimizedChatBot: boolean;
  setIsMinimizedChatBot: (value: boolean) => void;
}

const LasagnaChatBot: React.FC<LasagnaChatBotProps> = ({
  setIsOpenChatBot,
  isMinimizedChatBot,
  setIsMinimizedChatBot
}) => {
  const { t } = useTranslation();
  const initialMessages = [
    {
      id: 'bot-1',
      content: `I’m here to assist you. You can ask me questions, get information, or let me know what you’d like to do, and I’ll do my best to help.\nHow would you like to get started today?`,
      sender: 'bot',
      timestamp: new Date()
    }
  ];
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [isListening, setIsListening] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Socket handler for bot responses
  const onMessageReceived = useCallback((data: any) => {
    if (data && data.message) {
      const incomingMessage = {
        id: data.message_id || `bot-${Date.now()}`,
        content: data.message,
        sender: data?.meta?.sender ?? 'bot',
        timestamp: new Date()
      };
      const isUpdate = data.message !== '[DONE]';
      const rawChunk = data.message;

      if (incomingMessage.sender === 'bot') {
        setIsTyping(false);
      }
      if (rawChunk === '[DONE]') {
        setIsListening(false);
      }

      setMessages(prevMessages => {
        const existingIndex = prevMessages.findIndex(
          msg => msg.id === incomingMessage.id
        );

        if (isUpdate && existingIndex !== -1 && rawChunk !== '[DONE]') {
          const updated = [...prevMessages];
          updated[existingIndex] = {
            ...updated[existingIndex],
            content: updated[existingIndex].content + (rawChunk || '')
          };
          return updated;
        } else if (existingIndex === -1) {
          return [...prevMessages, incomingMessage];
        } else {
          return prevMessages;
        }
      });
    }
  }, []);

  useEffect(() => {
    socketService.subscribe(
      'onMessage',
      'lasagna-ai-response',
      onMessageReceived
    );
    return () => {
      socketService.unsubscribe('onMessage', 'lasagna-ai-response');
    };
  }, [onMessageReceived]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 36);
      textarea.style.height = `${newHeight}px`;
    }
  }, [input]);

  const sendHandler = async (messageText: string) => {
    const userMessage = {
      id: `user-${Date.now()}`,
      content: messageText,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    // Reset height after sending
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      setIsTyping(true);
      setIsListening(true);
      const params = {
        message: messageText,
        timezone: moment.tz.guess()
      };
      socketService?.getSocket()?.emit('lasagna-user-message', params);
    } catch (error) {
      console.error('Error sending', error);
      setMessages(prev => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          content: 'Sorry, there was an error connecting to the assistant.',
          sender: 'bot',
          timestamp: new Date()
        }
      ]);
      setIsTyping(false);
      setIsListening(false);
    }
  };

  return (
    <div
      className="fixed z-40 border shadow-lg right-0 bottom-0 bg-white rounded-xl w-80 mr-5"
      style={{ minHeight: isMinimizedChatBot ? 'auto' : 420 }}>
      <div className="flex items-center justify-between bg-white rounded-t-xl p-3 border-b">
        <div className="flex items-center">
          <SVGAssets.LasagnaChatIcon className="w-7 h-7" />
          <span className="ml-2 text-black font-bold text-base">
            {t('lasagnaBot')}!
          </span>
        </div>
        <div className="flex items-center">
          <SVGAssets.ChatMinimizeIcon
            className="w-4 h-4 text-black cursor-pointer mr-2"
            onClick={() => setIsMinimizedChatBot(!isMinimizedChatBot)}
          />
          <SVGAssets.ChatCloseIcon
            className="w-5 h-5 text-black cursor-pointer"
            onClick={() => setIsOpenChatBot(false)}
          />
        </div>
      </div>

      {!isMinimizedChatBot && (
        <div className="flex flex-col justify-between h-full">
          <div className="min-h-[290px]">
            <div className="flex flex-col gap-2 px-4 py-3 overflow-y-auto max-h-[300px]">
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`flex ${
                    msg.sender === 'user' ? 'justify-end' : 'justify-start'
                  }`}>
                  {msg.sender === 'bot' && (
                    <SVGAssets.LasagnaChatIcon className="w-7 h-7 mr-2 self-end" />
                  )}
                  <div
                    className={`rounded-2xl px-4 py-2 text-sm max-w-[70%] border-2 ${
                      msg.sender === 'user'
                        ? 'border-[#D9622A] text-[#D9622A] bg-white rounded-br-none'
                        : 'border-[#787878] bg-white text-gray-800 rounded-bl-none'
                    }`}>
                    <AIContentMarkdown>{msg.content}</AIContentMarkdown>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {isTyping && (
              <div className="px-4 pb-2">
                <div className="flex gap-1 items-center h-5">
                  {[0, 1, 2].map(i => (
                    <span
                      key={i}
                      data-testid="typing-dot"
                      className="block w-2 h-2 bg-[#FFB800] rounded-full animate-bounce"
                      style={
                        i > 0 ? { animationDelay: `${i * 150}ms` } : undefined
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center px-4 pb-4">
            <div className="w-full flex items-center justify-between bg-white rounded-full border border-[#FFAD00] px-4 py-2 shadow-sm mr-2">
              <textarea
                ref={textareaRef}
                rows={1}
                className="block w-full border-none resize-none focus:outline-none text-xs bg-transparent max-h-[36px] min-h-[20px]"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Ask a question"
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey && input.trim()) {
                    e.preventDefault();
                    sendHandler(input);
                  }
                }}
              />
              {input.trim() && (
                <Button
                  btnStyle="link"
                  data-testid="chat-sent-icon"
                  className="flex items-center justify-center ml-3 cursor-pointer"
                  tabIndex={0}
                  isDisabled={!input.trim() || isListening}
                  onClick={() => {
                    if (input.trim()) {
                      sendHandler(input);
                    }
                  }}>
                  <SVGAssets.ChatSentIcon className="h-5 w-5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LasagnaChatBot;
